/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { BrowserCacheLocation, LogLevel } from "@azure/msal-browser";

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */

const pillarCodes = ["ams", "cl", "cus", "rp", "tms"];

export const msalConfig = {
    enabled: import.meta.env.VITE_USE_MSAL?.length > 0,
    auth: {
        clientId: import.meta.env.VITE_MSAL_CLIENTID,
        authority: import.meta.env.VITE_MSAL_AUTHORITY,
        redirectUri: import.meta.env.DEV ? "http://localhost:8080" : import.meta.env.VITE_MSAL_REDIRECTURI,
    },
    cache: {
        cacheLocation: BrowserCacheLocation.SessionStorage, // This configures where your cache will be stored
        storeAuthStateInCookie: true, // Required for login redirect flow to work reliably across browsers
    },
    system: {	
        loggerOptions: {	
            loggerCallback: (level, message, containsPii) => {	
                if (containsPii) {		
                    return;		
                }		
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }	
            }	
        }	
    }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const getLoginRequests = () => {
    var listOfScopes = [];

    pillarCodes.forEach(pillarCode => {
        listOfScopes.push({
            scopes: [getScope(pillarCode)]
        })
    });

    return listOfScopes
};

export const getScope = (pillarCode: string) => {
    const environment = import.meta.env.VITE_SCOPE_ENVIRONMENT;
    
    var scopePrefix = ``;
    if (environment === "nft"){
        scopePrefix = `${getAzureAdTenantId()}/`;
    }

    return `api://${scopePrefix}${import.meta.env.VITE_SCOPE_INSTANCE}-${pillarCode}-${environment}/.default`;
};

export const getAzureAdTenantId = () => {
    const authorityParts = import.meta.env.VITE_MSAL_AUTHORITY.split("/");
    return authorityParts[authorityParts.length - 1];
};